import founder from '../data/founder.json';
import { isFilled } from '../utils/contentFields';

// ── NorbiPhone üzlet-adatok – SINGLE SOURCE OF TRUTH ─────────────────────────
// Minden NAP-adat (név/cím/telefon/nyitvatartás) innen jöjjön. Ha változik egy
// telefonszám vagy nyitvatartás, CSAK itt kell átírni (+ a statikus
// public/index.html JSON-LD-ben, ami build-time fájl és nem tud importálni).

export const BUSINESS = {
  name: 'NorbiPhone',
  url: 'https://norbiphone.hu',
  id: 'https://norbiphone.hu/#business', // kanonikus schema @id – minden JSON-LD erre hivatkozzon
  email: 'hello@norbiphone.hu',
  image: 'https://norbiphone.hu/og-image.jpg',
  logo: 'https://norbiphone.hu/logo-face.png',
  facebook: 'https://www.facebook.com/norbiphone',
  instagram: 'https://www.instagram.com/norbiphone',
  tiktok: 'https://www.tiktok.com/@norbiphone',
  priceRange: '$$',
  // ── Cégadatok ──────────────────────────────────────────────────────────────
  // FONTOS: a „NorbiPhone” MÁRKANÉV, nem önálló jogi entitás – a két üzletet
  // KÉT KÜLÖN egyéni vállalkozó üzemelteti (lásd OPERATORS). Ezért a
  // hivatalos név / adószám / székhely fogalmilag NEM a márkához tartozik,
  // hanem telephelyenként külön: a `buildStoreNode` az OPERATORS-ból teszi a
  // megfelelő LocalBusiness node-ra (legalName + taxID). Itt szándékosan
  // üresen hagyjuk – kitalált „cégadat” nem kerülhet a strukturált adatba.
  legalName: '',        // ld. OPERATORS[].name (telephelyenként)
  taxId: '',            // ld. OPERATORS[].taxId (telephelyenként)
  registeredOffice: '', // ld. OPERATORS[].registeredOffice (telephelyenként)
  // A márka indulása = az ELSŐ üzlet nyitása (belvárosi, 2020). A telephelyek
  // saját nyitási éve a STORES[].openedYear mezőben van, és a LocalBusiness
  // node-okra kerül külön – a Tesco üzletsori üzlet 2022-ben nyitott.
  foundedYear: 2020,
};

// ── Üzemeltetők (Ekertv. 4. § – impresszum-kötelezettség) ────────────────────
// A két üzletet KÉT KÜLÖN egyéni vállalkozó üzemelteti; MINDKETTŐT fel kell
// tüntetni. A két adatkezelő NEM közös adatkezelő: mindegyik önállóan felel a
// saját üzletében kezelt személyes adatokért.
export const OPERATORS = [
  {
    storeKey: 'belvaros',
    storeLabel: 'NorbiPhone Belvárosi üzlet',
    name: 'Papp Norbert egyéni vállalkozó (NorbiPhone)',
    registrationNumber: '55142847',
    taxId: '56493524-1-24',
    registeredOffice: '5900 Orosháza, Rákóczi út 16.',
    email: 'hello@norbiphone.hu',
    phone: '+36308787007',
    phoneDisplay: '+36 30 878 7007',
  },
  {
    storeKey: 'tesco',
    storeLabel: 'NorbiPhone Orosháza Tesco üzlet',
    name: 'Hajdú Zsaklin egyéni vállalkozó (NorbiPhone)',
    registrationNumber: '57425674',
    taxId: '59401520-1-24',
    registeredOffice: '5900 Orosháza, Vásárhelyi út 23 A ép.',
    email: 'norbiphonemail@gmail.com',
    phone: '+36309100234',
    phoneDisplay: '+36 30 910 0234',
  },
];

// ── Tárhelyszolgáltató (Ekertv. 4. § – impresszum) ───────────────────────────
export const HOSTING_PROVIDER = {
  name: 'Fekete Zsolt e.v. (tárhelyszolgáltató)',
  email: 'hello@zsoltfekete.hu',
  service: 'Tárhelyszolgáltatás – a weboldal és az adatbázis működtetéséhez szükséges szerver-infrastruktúra biztosítása (az adatfeldolgozás célja ezen infrastruktúra üzemeltetése)',
  location: 'Magyarország – 6721 Szeged, Dugonics utca 25. 1/1',
  legalBasis: 'az adatkezelő adatkezelésének jogalapjához igazodik; az adatfeldolgozó az adatkezelő megbízása alapján, önálló jogalap nélkül végzi tevékenységét',
};

// Kiszolgálási terület (Local SEO) – Orosháza + Békés megye. A schema areaServed-je
// ezt tükrözi; NEM kulcsszóhalmozás, a valós vonzáskörzet.
export const AREA_SERVED = [
  { '@type': 'City', name: 'Orosháza' },
  { '@type': 'AdministrativeArea', name: 'Békés megye' },
];

export const STORES = {
  belvaros: {
    key: 'belvaros',
    id: 'belvaros',
    name: 'NorbiPhone Belvárosi üzlet',
    // Rövid, ügyfélnek szóló címke (terméklista, terméklap, hívás-sávok). A
    // ragozott alak azért külön mező, mert magyarul a „Hívd a … üzletet” csak
    // tárgyesetben helyes – sablonból összefűzve hibás lenne.
    shortLabel: 'Belvárosi üzlet',
    shortLabelAccusative: 'Belvárosi üzletet',
    // A telephely kanonikus oldala a /uzletek – az @id és az url is oda mutat.
    schemaId: 'https://norbiphone.hu/uzletek#belvaros',
    pageUrl: 'https://norbiphone.hu/uzletek/#belvaros',
    placeId: 'ChIJmzzl0Cg_REcRMqqmM6-m1pM', // Google Business Profile place ID
    phone: '+36308787007',
    phoneDisplay: '+36 30 878 7007',
    address: { street: 'Rákóczi út 16', city: 'Orosháza', zip: '5900', country: 'HU' },
    facebook: 'https://www.facebook.com/NorbiPhone',
    openedYear: 2020,          // ez az üzlet nyitott először → a márka alapítása
    // H–P 9–12 / 13–17 (ebédszünet), Szo 9–12
    hoursDisplay: 'H–P 9:00–17:00 · Szo 9:00–12:00',
    lunchBreak: '12:00–13:00',
    openingHours: [
      { days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'], opens: '09:00', closes: '17:00' },
      { days: ['Saturday'], opens: '09:00', closes: '12:00' },
    ],
    // ── Sprint1 Menet1: percre pontos ADAT (az ebédszünet KÜLÖN intervallum).
    // FIGYELEM: a nyitva/zárva LOGIKA (isAnyStoreOpenNow) szándékosan még a
    // fenti openingHours-ból számol – az átállása külön sprint, külön teszttel.
    openingIntervals: [
      { days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'], intervals: [['09:00', '12:00'], ['13:00', '17:00']] },
      { days: ['Saturday'], intervals: [['09:00', '12:00']] },
    ],
    closedDays: ['Sunday'],
    geo: { lat: 46.5576868, lng: 20.6657352 },
    mapsUrl: 'https://www.google.com/maps/dir/?api=1&destination=46.5576868,20.6657352',
    // Google Business Profile – SAJÁT profil. A `reviewUrl` a véleményíró
    // mélylink, a `mapUrl` a rövid GBP/Maps link. A kettőt SOHA ne keverd a
    // két üzlet között (lásd FELADAT 5: nincs kereszthivatkozás).
    reviewUrl: 'https://g.page/r/CTKqpjOvptaTEAE/review',
    mapUrl: 'https://maps.app.goo.gl/nbvk9BJV7fcQruFd8',
  },
  tesco: {
    key: 'tesco',
    id: 'tesco',
    name: 'NorbiPhone Tesco',
    shortLabel: 'Tesco üzletsor',
    shortLabelAccusative: 'Tesco üzletsort',
    schemaId: 'https://norbiphone.hu/uzletek#tesco',
    pageUrl: 'https://norbiphone.hu/uzletek/#tesco',
    placeId: 'ChIJ0Z9K2bRvREcRhcZWKBMmtnk', // Google Business Profile place ID
    phone: '+36309100234',
    phoneDisplay: '+36 30 910 0234',
    address: { street: 'Vásárhelyi út 23/A', city: 'Orosháza', zip: '5900', country: 'HU' },
    facebook: 'https://www.facebook.com/norbiphonetesco/',
    openedYear: 2022,
    hoursDisplay: 'H–P 9:00–19:00 · Szo 9:00–18:00',
    openingHours: [
      { days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'], opens: '09:00', closes: '19:00' },
      { days: ['Saturday'], opens: '09:00', closes: '18:00' },
    ],
    openingIntervals: [
      { days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'], intervals: [['09:00', '19:00']] },
      { days: ['Saturday'], intervals: [['09:00', '18:00']] },
    ],
    closedDays: ['Sunday'],
    geo: { lat: 46.5480352, lng: 20.6491554 },
    mapsUrl: 'https://www.google.com/maps/dir/?api=1&destination=46.5480352,20.6491554',
    reviewUrl: 'https://g.page/r/CYXGVigTJrZ5EAE/review',
    mapUrl: 'https://maps.app.goo.gl/XihDJkUMJ9PFqBG49',
  },
};

// Fő kontakt-szám (Belváros) – bolt-független CTA-khoz (fejléc, footer, 404 …).
// Ha egy CTA egy KONKRÉT készülékhez tartozik, NE ezt használd, hanem a
// getStoreContact(device.store)-t – lásd lentebb.
export const PRIMARY_PHONE = STORES.belvaros.phone;
export const PRIMARY_PHONE_DISPLAY = STORES.belvaros.phoneDisplay;
export const MESSENGER_URL = 'https://m.me/NorbiPhone';

// ── Bolt-feloldás (Sprint 1.1 / FELADAT 1) ───────────────────────────────────
// Az office API a boltot a `f_5rklnfbx49r` mezőben adja: 1 = Belváros, 2 = Tesco.
// EZ az egyetlen hely, ahol ez a leképezés él – a komponensek ne ismételjék.
export const OFFICE_STORE_FIELD_MAP = { 1: 'belvaros', 2: 'tesco' };

/** Nyers bolt-érték → kanonikus kulcs ('belvaros' | 'tesco' | null).
 *  Elfogad: kulcsot, office-kódot (1/2), és emberi címkét ("Tesco üzletsor",
 *  "NorbiPhone Belváros"). Ismeretlen/hiányzó érték → null (NEM tippelünk). */
export const resolveStoreKey = (raw) => {
  if (raw === null || raw === undefined || raw === '') return null;
  if (typeof raw === 'number') return OFFICE_STORE_FIELD_MAP[raw] || null;
  const v = String(raw).trim().toLowerCase();
  if (OFFICE_STORE_FIELD_MAP[v]) return OFFICE_STORE_FIELD_MAP[v];
  if (v.includes('tesco')) return 'tesco';
  if (v.includes('belv')) return 'belvaros';
  return null;
};

/** A hétköznapi sáv rövid alakja a bizalmi soroknak: „H–P 9–17”.
 *  Az `openingHours`-ból SZÁMOL (nincs kézzel írt második nyitvatartás), a
 *  kerek órákról levágja a „:00”-t, hogy a mondatba beleférjen. Ismeretlen
 *  nyitvatartásnál üres stringet ad – ilyenkor a hívó ne írjon ki semmit. */
export const storeWeekdayHoursShort = (store) => {
  const row = ((store && store.openingHours) || []).find((r) => r.days.includes('Monday'));
  if (!row) return '';
  const t = (v) => String(v).replace(/^0/, '').replace(/:00$/, '');
  return `H–P ${t(row.opens)}–${t(row.closes)}`;
};

/** Egy készülék/üzlet TELJES kontakt-csomagja EGY helyről: telefon, GBP-link,
 *  útvonal. Ismeretlen bolt esetén a fő számra esik vissza, `resolved:false`
 *  jelzéssel – így a hívógomb sosem törik el, de mérhető marad, hogy nem
 *  bolt-specifikus. A `source` a GA4/PostHog `store` paraméterébe megy. */
export const getStoreContact = (rawStore) => {
  const key = resolveStoreKey(rawStore);
  const s = key ? STORES[key] : STORES.belvaros;
  return {
    key,                                  // null, ha nem feloldható
    resolved: Boolean(key),
    store: key || 'unknown',              // analytics-barát címke
    name: s.name,
    shortLabel: s.shortLabel,             // „Belvárosi üzlet” – ügyfélnek szóló rövid név
    shortLabelAccusative: s.shortLabelAccusative,  // „…üzletet” – mondatba fűzéshez
    weekdayHours: storeWeekdayHoursShort(s),       // „H–P 9–17”
    phone: s.phone,                       // tel: href-hez (szóköz nélkül)
    phoneDisplay: s.phoneDisplay,         // megjelenítéshez
    telHref: `tel:${s.phone}`,
    // Egységes aria-label a hívás-linkekre. A LÁTHATÓ számot is tartalmazza,
    // mert a WCAG 2.5.3 (Label in Name) szerint az elérhető névnek magában
    // kell foglalnia a látható szöveget – különben a hangvezérlés nem találja.
    callAriaLabel: `${s.shortLabel} hívása: ${s.phoneDisplay}`,
    reviewUrl: s.reviewUrl,               // SAJÁT GBP profil
    mapUrl: s.mapUrl,                     // rövid GBP/Maps link
    directionsUrl: s.mapsUrl,             // útvonaltervezés
    messengerUrl: MESSENGER_URL,
  };
};

// ── JSON-LD builderek ─────────────────────────────────────────────────────────

/** Kitöltött-e a mező (a TODO_KITOLTENDO placeholder nem az). */
// A helper a utils/contentFields-ből jön – korábban itt volt egy SZÓ SZERINTI
// második másolata, ami a TODO_-konvenció megváltozásakor szétcsúszhatott volna.

const storeAddressNode = (s) => ({
  '@type': 'PostalAddress',
  streetAddress: s.address.street,
  addressLocality: s.address.city,
  postalCode: s.address.zip,
  addressCountry: s.address.country,
});

/** OpeningHoursSpecification node-ok a KANONIKUS `openingIntervals`-ből.
 *
 *  Miért innen: az ebédszünet így KÉT külön intervallum (9–12 és 13–17), nem
 *  egy hamis 9–17 blokk. A `dayOfWeek` MINDIG tömb (a korábbi statikus blokk
 *  szombatnál stringet írt – a Google emiatt eltérő adatot látott két helyről).
 *  A zárva napok expliciten deklaráltak (`opens=closes=00:00`), hogy a Google
 *  ne következtetésre legyen utalva. */
const storeHoursNodes = (s) => {
  const nodes = [];
  (s.openingIntervals || []).forEach((g) => {
    g.intervals.forEach(([opens, closes]) => {
      nodes.push({
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: [...g.days],
        opens,
        closes,
      });
    });
  });
  (s.closedDays || []).forEach((day) => {
    nodes.push({
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: [day],
      opens: '00:00',
      closes: '00:00',
    });
  });
  return nodes;
};

/** Egy üzlet önálló LocalBusiness/MobilePhoneStore node-ja, saját helyes
 *  cím+telefon+nyitvatartás párosítással (NAP-konzisztencia). */
export const buildStoreNode = (storeKey) => {
  const s = STORES[storeKey];
  // Az üzletet üzemeltető egyéni vállalkozó hivatalos adatai (Ekertv./impresszum
  // forrás) – a telephely-node-ra kerülnek, mert a jogi entitás ITT értelmezhető,
  // nem a márka szintjén. Kitöltetlen mező nem kerül ki.
  const op = OPERATORS.find((o) => o.storeKey === storeKey) || {};
  const sameAs = [BUSINESS.facebook];
  const mapsUrl = s.placeId ? `https://www.google.com/maps/place/?q=place_id:${s.placeId}` : null;
  if (mapsUrl) sameAs.unshift(mapsUrl);
  return {
    '@type': ['LocalBusiness', 'MobilePhoneStore'],
    '@id': s.schemaId,
    name: s.name,
    ...(isFilled(op.name) ? { legalName: op.name } : {}),
    ...(s.openedYear ? { foundingDate: String(s.openedYear) } : {}),
    ...(isFilled(op.taxId) ? { taxID: op.taxId } : {}),
    ...(isFilled(op.email) ? { email: op.email } : {}),
    parentOrganization: { '@id': BUSINESS.id },
    // Telephelyenként SAJÁT url + hasMap (a place_id-s Maps-link) – eddig
    // mindkét node a főoldalra mutatott, ami nem különböztette meg őket.
    url: s.pageUrl || BUSINESS.url,
    ...(mapsUrl ? { hasMap: mapsUrl } : {}),
    image: BUSINESS.image,
    logo: BUSINESS.logo,
    telephone: s.phone,
    priceRange: BUSINESS.priceRange,
    address: storeAddressNode(s),
    geo: { '@type': 'GeoCoordinates', latitude: s.geo.lat, longitude: s.geo.lng },
    openingHoursSpecification: storeHoursNodes(s),
    areaServed: AREA_SERVED,
    sameAs,
  };
};

/** Oldal-szintű @graph (Sprint 1.1 / FELADAT 6).
 *
 *  Egy oldalon EGY JSON-LD blokk legyen, benne a kanonikus vállalkozás-node-ok
 *  (Organization + 2 üzlet) ÉS az oldal saját node-jai (Service, FAQPage,
 *  BreadcrumbList, …). Így:
 *   • a `{ '@id': BUSINESS.id }` hivatkozások NEM lógnak a levegőben,
 *   • a vállalkozás definíciója egy helyről jön → nem tud szétcsúszni,
 *   • nincs ugyanaz az entitás több, egymásnak ellentmondó blokkban.
 *
 *  A gyerek-node-okból a `@context` kikerül (a @graph gyökerén van egyszer).
 */
/** Nyitva van-e MOST bármelyik üzlet – a fenti openingHours-ból számolva
 *  (egy igazságforrás, nincs második nyitvatartás-lista). Vasárnap egyik
 *  üzletnek sincs bejegyzése → zárva. */
export const isAnyStoreOpenNow = (now = new Date()) => {
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const day = dayNames[now.getDay()];
  const cur = now.getHours() * 60 + now.getMinutes();
  const toMin = (hhmm) => {
    const [h, m] = String(hhmm).split(':').map(Number);
    return h * 60 + m;
  };
  return Object.values(STORES).some((store) =>
    (store.openingHours || []).some(
      (r) => r.days.includes(day) && cur >= toMin(r.opens) && cur < toMin(r.closes)
    )
  );
};

// ── Üzlet-szintű „most nyitva / most zárva” ──────────────────────────────────
// UGYANABBÓL az `openingHours`-ból számol, mint az `isAnyStoreOpenNow` és a
// JSON-LD `openingHoursSpecification` – egy igazságforrás, nincs második
// nyitvatartás-lista.
//
// Miért kell: az /uzletek oldal eddig CSAK a statikus sorokat mutatta
// („H-P: 9:00-17:00”, „Ebédszünet: 12:00-13:00”). Külső auditon mérve: aki
// hétköznap fél egykor kereste a belvárosi üzletet, semmilyen jelzést nem
// kapott arról, hogy épp ebédszünet van – ugyanúgy hívta a számot, és azt
// tapasztalta, hogy „nem veszik fel”. Ez a fajta kár néma: nem jelenik meg
// semmilyen mérőszámban, mert az ember nem panaszkodik, csak nem jön vissza.

const _DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const _toMin = (hhmm) => {
  const [h, m] = String(hhmm).split(':').map(Number);
  return h * 60 + m;
};
const _fmt = (min) => `${Math.floor(min / 60)}:${String(min % 60).padStart(2, '0')}`;

/**
 * Egy üzlet pillanatnyi állapota.
 * @returns {{open: boolean, label: string}}
 *   `open`  – nyitva van-e MOST
 *   `label` – kiírható szöveg, pl. „Most nyitva · 17:00-ig”,
 *             „Most ebédszünet · 13:00-tól”, „Most zárva · holnap 9:00-tól”
 * Ismeretlen/hiányzó nyitvatartásnál `{open:false, label:''}` – ilyenkor a hívó
 * NE írjon ki semmit (jobb a csend, mint a téves állítás).
 */
export const storeOpenState = (store, now = new Date()) => {
  // A PERCRE PONTOS adat az `openingIntervals` (ott az ebédszünet külön
  // intervallum); az `openingHours` a durvább, egy-sávos változat, amit a
  // JSON-LD és az `isAnyStoreOpenNow` használ.
  //
  // Az `isAnyStoreOpenNow`-t SZÁNDÉKOSAN nem állítjuk át (a `MobileCTABar`
  // hívás/visszahívás váltása épül rá, az külön döntés és külön teszt).
  // A MEGJELENÍTÉS viszont a pontos adatot kapja – épp az ebédszünet volt az,
  // amiről a látogató semmit nem tudott meg.
  const dayName = _DAY_NAMES[now.getDay()];
  const precise = (store && store.openingIntervals) || null;
  const rows = precise || (store && store.openingHours) || [];
  if (!rows.length) return { open: false, closesAt: null, label: '' };

  const cur = now.getHours() * 60 + now.getMinutes();
  const todays = (precise
    ? rows
      .filter((r) => r.days.includes(dayName))
      .flatMap((r) => (r.intervals || []).map(([o, c]) => ({ from: _toMin(o), to: _toMin(c) })))
    : rows
      .filter((r) => r.days.includes(dayName))
      .map((r) => ({ from: _toMin(r.opens), to: _toMin(r.closes) }))
  ).sort((a, b) => a.from - b.from);

  const current = todays.find((r) => cur >= r.from && cur < r.to);
  // `closesAt` külön is kell (nem csak a kész címkében): a /hivtal oldal ebből
  // írja ki, meddig ér el minket a hívó ANNÁL az üzletnél, amelyiknek a számát
  // felkínálja – lásd openStoreContact.
  if (current) return { open: true, closesAt: _fmt(current.to), label: `Most nyitva · ${_fmt(current.to)}-ig` };

  // Ma még nyit? (Ebédszünet és a nyitás előtti időszak is ide esik.)
  const upcoming = todays.find((r) => cur < r.from);
  if (upcoming) {
    // Ebédszünet = ma már volt egy zárt sáv előtte. Ezt külön nevezzük meg,
    // mert az ügyfél számára ez mást jelent, mint a „még nem nyitottunk”.
    const isBreak = todays.some((r) => r.to <= cur);
    return {
      open: false,
      closesAt: null,
      label: isBreak ? `Most ebédszünet · ${_fmt(upcoming.from)}-tól` : `Ma ${_fmt(upcoming.from)}-tól nyit`,
    };
  }

  // Ma már bezárt (vagy ma zárva van) – megkeressük a következő nyitónapot.
  for (let i = 1; i <= 7; i += 1) {
    const d = _DAY_NAMES[(now.getDay() + i) % 7];
    const next = rows
      .filter((r) => r.days.includes(d))
      .flatMap((r) => (precise ? (r.intervals || []).map(([o]) => _toMin(o)) : [_toMin(r.opens)]))
      .sort((a, b) => a - b)[0];
    if (next === undefined) continue;
    const when = i === 1 ? 'holnap' : ['vasárnap', 'hétfőn', 'kedden', 'szerdán', 'csütörtökön', 'pénteken', 'szombaton'][(now.getDay() + i) % 7];
    return { open: false, closesAt: null, label: `Most zárva · ${when} ${_fmt(next)}-tól` };
  }
  return { open: false, closesAt: null, label: 'Most zárva' };
};

/**
 * ANNAK az üzletnek az elérhetőségei, ahol MOST fel is veszik a telefont.
 *
 * A két üzlet nyitvatartása eltér (a Belvárosi 17:00-kor zár, a Tesco
 * 19:00-kor). A nem fogadott hívás utáni landing (`/hivtal`) ezért nem
 * adhatja vakon a fő számot: a látogató épp azt hívta hiába. Sorrend:
 *   1. ha a Belvárosi (fő szám) nyitva van, az marad,
 *   2. különben a legelső NYITVA lévő üzlet,
 *   3. ha egyik sincs nyitva, a fő szám (a zárt állapot úgyis űrlapot kínál).
 */
export const openStoreContact = (now = new Date()) => {
  const pick = storeOpenState(STORES.belvaros, now).open
    ? STORES.belvaros
    : (Object.values(STORES).find((s) => storeOpenState(s, now).open) || STORES.belvaros);
  // A `closesAt` ENNEK az üzletnek a mai zárása – NEM a vállalkozásé. A kettő
  // eltér: a Belvárosi 17:00-kor zár, a Tesco 19:00-kor. Korábban a /hivtal a
  // vállalkozás záróidejét írta ki (19:00) a Belvárosi szám fölé, ami 17:00
  // után már nem szólt volna – a hívó hiába hívott volna újra.
  return { ...getStoreContact(pick.key), closesAt: storeOpenState(pick, now).closesAt };
};

/**
 * ÜNNEPNAPOK / rendkívüli zárva tartás.
 *
 * SZÁNDÉKOSAN ÜRES: nem találunk ki dátumokat. Amikor tudod, mikor lesz zárva
 * (ünnep, leltár, szabadság), vedd fel ide `YYYY-MM-DD` alakban – a
 * `businessOpenState` innentől zártnak veszi azt a napot, és a következő
 * nyitást is ezek kihagyásával számolja.
 *
 * Példa: ['2026-12-24', '2026-12-25', '2026-12-26']
 */
export const CLOSED_DATES = [];

const _isoDate = (d) => {
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

/** Az adott napon nyitva tartunk-e egyáltalán (ünnepnap-ellenőrzéssel). */
const _closedDate = (d) => CLOSED_DATES.includes(_isoDate(d));

/** Egy üzlet adott napi nyitvatartási sávjai percben, rendezve. */
const _dayIntervals = (store, date) => {
  if (_closedDate(date)) return [];
  const dayName = _DAY_NAMES[date.getDay()];
  const rows = (store && store.openingIntervals) || [];
  return rows
    .filter((r) => r.days.includes(dayName))
    .flatMap((r) => (r.intervals || []).map(([o, c]) => ({ from: _toMin(o), to: _toMin(c) })))
    .sort((a, b) => a.from - b.from);
};

const _HU_DAYS = ['vasárnap', 'hétfőn', 'kedden', 'szerdán', 'csütörtökön', 'pénteken', 'szombaton'];

/**
 * A VÁLLALKOZÁS pillanatnyi állapota – nem egy üzleté, hanem a kettőé együtt.
 * Ezt használja a /nyitva oldal, ami SMS-ből érkező, nem fogadott hívást fogad.
 *
 * Nyitva vagyunk, ha BÁRMELYIK üzlet nyitva van; a záráshoz a legkésőbb záró
 * üzletet vesszük (a hívó azt akarja tudni, meddig érhet még el minket).
 * Zárva: a következő nyitás mindkét üzletet nézve, a legkorábbi.
 *
 * HIBA ESETÉN SZÁNDÉKOSAN „zárva”: az a biztonságosabb, mert űrlapot kínál a
 * látogatónak ahelyett, hogy hiába hívna újra.
 *
 * @returns {{open: boolean, closesAt: string|null, nextOpenAt: string|null,
 *            nextOpenLabel: string|null, storeName: string|null}}
 */
export const businessOpenState = (now = new Date()) => {
  try {
    const stores = Object.values(STORES);
    const cur = now.getHours() * 60 + now.getMinutes();

    // ── Nyitva? A legkésőbb záró nyitott üzlet számít. ──
    let closesAt = -1;
    let openStore = null;
    stores.forEach((s) => {
      _dayIntervals(s, now).forEach((iv) => {
        if (cur >= iv.from && cur < iv.to && iv.to > closesAt) {
          closesAt = iv.to;
          openStore = s;
        }
      });
    });
    if (openStore) {
      return {
        open: true,
        closesAt: _fmt(closesAt),
        nextOpenAt: null,
        nextOpenLabel: null,
        storeName: openStore.name,
      };
    }

    // ── Zárva: a legkorábbi következő nyitás, max. 14 napra előre. ──
    for (let i = 0; i <= 14; i += 1) {
      const d = new Date(now.getTime());
      d.setDate(d.getDate() + i);
      let best = null;
      stores.forEach((s) => {
        _dayIntervals(s, d).forEach((iv) => {
          // Ma csak a MÉG hátralévő sáv számít (pl. ebédszünet után).
          if (i === 0 && iv.from <= cur) return;
          if (best === null || iv.from < best) best = iv.from;
        });
      });
      if (best === null) continue;
      const when = i === 0 ? 'Ma' : i === 1 ? 'Holnap' : _HU_DAYS[d.getDay()].replace(/^./, (c) => c.toUpperCase());
      return {
        open: false,
        closesAt: null,
        nextOpenAt: _fmt(best),
        nextOpenLabel: `${when} ${_fmt(best)}-kor nyitunk`,
        storeName: null,
      };
    }
    return { open: false, closesAt: null, nextOpenAt: null, nextOpenLabel: null, storeName: null };
  } catch {
    // Bármi hiba → zárva. Így legalább az űrlapot felkínáljuk.
    return { open: false, closesAt: null, nextOpenAt: null, nextOpenLabel: null, storeName: null };
  }
};

export const buildPageGraph = (...nodes) => {
  const strip = (n) => {
    if (!n || typeof n !== 'object') return null;
    const { '@context': _ctx, ...rest } = n;
    return rest;
  };
  const base = buildBusinessGraph();
  return {
    '@context': 'https://schema.org',
    '@graph': [...base['@graph'], ...nodes.map(strip).filter(Boolean)],
  };
};

/** A teljes vállalkozás-gráf: 1 Organization + 2 helyes üzlet-node egy @graph-ban.
 *  Ez az EGYETLEN hely, ahol a teljes definíció megjelenik – más oldalak csak
 *  { "@id": BUSINESS.id } referenciát használjanak. */
export const buildBusinessGraph = (extra = {}) => ({
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Organization',
      '@id': BUSINESS.id,
      name: BUSINESS.name,
      // A hivatalos név csak akkor kerül ki, ha tényleg meg van adva – a
      // TODO_KITOLTENDO placeholder SOHA nem szivároghat a strukturált adatba.
      ...(isFilled(BUSINESS.legalName) ? { legalName: BUSINESS.legalName } : {}),
      ...(isFilled(BUSINESS.taxId) ? { taxID: BUSINESS.taxId } : {}),
      ...(isFilled(BUSINESS.foundedYear) ? { foundingDate: String(BUSINESS.foundedYear) } : {}),
      url: BUSINESS.url,
      email: BUSINESS.email,
      telephone: PRIMARY_PHONE,
      logo: `${BUSINESS.url}/logo-face.png`,
      image: BUSINESS.image,
      // A márka postai címe a fő kontakt-telephelyé (belvárosi üzlet) – ez az
      // a cím, ahol a NorbiPhone mint márka elérhető. A telephelyek saját
      // címe a LocalBusiness node-okon van.
      address: storeAddressNode(STORES.belvaros),
      // Kapcsolatfelvételi pont: mindkét üzlet száma, magyar nyelven.
      contactPoint: Object.values(STORES).map((s) => ({
        '@type': 'ContactPoint',
        contactType: 'customer service',
        telephone: s.phone,
        email: BUSINESS.email,
        areaServed: 'HU',
        availableLanguage: ['hu'],
      })),
      sameAs: [BUSINESS.facebook, BUSINESS.instagram, BUSINESS.tiktok].filter(Boolean),
      ...(isFilled(founder.name) ? { founder: { '@id': `${BUSINESS.url}/#founder` } } : {}),
      ...extra,
    },
    // Alapító – E-E-A-T. A gráfban eddig EGYETLEN Person entitás sem volt, így a
    // válaszmotoroknak nem volt kihez kötni a szakértelmet. Az adat a
    // data/founder.json-ból jön; kitöltetlen név esetén a node ki sem kerül.
    ...(isFilled(founder.name)
      ? [{
          '@type': 'Person',
          '@id': `${BUSINESS.url}/#founder`,
          name: founder.name,
          ...(isFilled(founder.role) ? { jobTitle: founder.role } : {}),
          worksFor: { '@id': BUSINESS.id },
          ...(isFilled(founder.portrait && founder.portrait.src)
            ? { image: `${BUSINESS.url}${founder.portrait.src}` } : {}),
        }]
      : []),
    {
      '@type': 'WebSite',
      '@id': `${BUSINESS.url}/#website`,
      url: BUSINESS.url,
      name: BUSINESS.name,
      inLanguage: 'hu-HU',
      publisher: { '@id': BUSINESS.id },
    },
    buildStoreNode('belvaros'),
    buildStoreNode('tesco'),
  ],
});

// ── Megjelenítési helperek (Sprint1 Menet1) – Footer és /uzletek innen kap ───
// formázott adatot, hogy a cím/nyitvatartás SOHA ne legyen kézzel beírva.

/** Egysoros magyar címformátum: "5900 Orosháza, Rákóczi út 16". */
export const storeAddressDisplay = (store) =>
  `${store.address.zip} ${store.address.city}, ${store.address.street}`;

const HU_DAY_GROUP = (days) => {
  const key = days.join(',');
  if (key === 'Monday,Tuesday,Wednesday,Thursday,Friday') return 'H-P';
  if (key === 'Saturday') return 'Szo';
  if (key === 'Sunday') return 'V';
  return key; // ismeretlen csoport: nyers érték, ne hazudjunk címkét
};

/** Nyitvatartás sorok a /uzletek kártyáihoz: {label, time, highlight?}[].
 *  Az openingHours + lunchBreak + closedDays ADATBÓL épül (nem kézzel írt). */
export const storeHoursRows = (store) => {
  const rows = [];
  (store.openingHours || []).forEach((g) => {
    rows.push({ label: HU_DAY_GROUP(g.days), time: `${g.opens.replace(/^0/, '')}-${g.closes.replace(/^0/, '')}` });
    if (g.days.includes('Monday') && store.lunchBreak) {
      rows.push({ label: 'Ebédszünet', time: store.lunchBreak.replace('–', '-'), highlight: true });
    }
  });
  (store.closedDays || []).forEach((d) => {
    rows.push({ label: HU_DAY_GROUP([d]), time: 'Zárva' });
  });
  return rows;
};
